import React, { useState, useEffect } from 'react';
import UserLink from './UserLink';
import {
  Plus, Mail, ArrowRight, UserCheck, UserMinus, Trash2, RotateCcw,
  GitMerge, Laptop, MessageSquare, History,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const FIELD_LABELS = {
  status: 'status',
  priority: 'priority',
  department: 'department',
  resolution: 'resolution notes',
};

const when = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// Turn one audit row into an icon, a colour and a sentence. Kept in one place
// so the vocabulary stays consistent -- an event type that isn't handled here
// still renders something readable rather than disappearing from the history.
const describe = (ev, viewer, onOpenUser) => {
  const actor = (
    <UserLink
      id={ev.actor_id}
      name={ev.actor}
      viewer={viewer}
      onOpen={onOpenUser}
      fallback="Someone"
    />
  );

  switch (ev.event_type) {
    case 'created':
      return { icon: Plus, tone: 'text-blue-600 dark:text-blue-400',
        text: <><b>{actor}</b> opened this ticket</> };

    case 'created_by_email':
      return { icon: Mail, tone: 'text-blue-600 dark:text-blue-400',
        text: <>Opened by email{ev.actor ? <> from <b>{actor}</b></> : ''}</> };

    case 'field_changed': {
      const label = FIELD_LABELS[ev.field] || ev.field;
      if (ev.field === 'resolution') {
        return { icon: ArrowRight, tone: 'text-n-500',
          text: <><b>{actor}</b> {ev.old_value ? 'updated' : 'added'} {label}</> };
      }
      return {
        icon: ArrowRight, tone: 'text-n-500',
        text: (
          <>
            <b>{actor}</b> changed {label} from{' '}
            <span className="font-medium text-n-900">{ev.old_value || 'none'}</span> to{' '}
            <span className="font-medium text-n-900">{ev.new_value || 'none'}</span>
          </>
        ),
      };
    }

    case 'assigned':
      if (!ev.new_value) {
        return { icon: UserMinus, tone: 'text-n-500',
          text: <><b>{actor}</b> unassigned this{ev.old_value ? <> from {ev.old_value}</> : ''}</> };
      }
      return {
        icon: UserCheck, tone: 'text-green-600 dark:text-green-400',
        text: ev.old_value
          ? <><b>{actor}</b> reassigned this from {ev.old_value} to <b>{ev.new_value}</b></>
          : <><b>{actor}</b> assigned this to <b>{ev.new_value}</b></>,
      };

    case 'deleted':
      return { icon: Trash2, tone: 'text-red-600 dark:text-red-400',
        text: <><b>{actor}</b> deleted this ticket</> };

    case 'restored':
      return { icon: RotateCcw, tone: 'text-green-600 dark:text-green-400',
        text: <><b>{actor}</b> restored this ticket</> };

    case 'merged_away':
      return { icon: GitMerge, tone: 'text-purple-600 dark:text-purple-400',
        text: <><b>{actor}</b> merged this into #{ev.new_value}</> };

    case 'merged_in':
      return { icon: GitMerge, tone: 'text-purple-600 dark:text-purple-400',
        text: <><b>{actor}</b> merged #{ev.new_value} into this ticket</> };

    case 'device_linked':
      return { icon: Laptop, tone: 'text-n-500', text: <><b>{actor}</b> linked a device</> };

    case 'device_cleared':
      return { icon: Laptop, tone: 'text-n-500', text: <><b>{actor}</b> removed the linked device</> };

    case 'comment':
      return { icon: MessageSquare, tone: 'text-n-500',
        text: <><b>{actor}</b> commented</>, body: ev.new_value };

    default:
      return { icon: ArrowRight, tone: 'text-n-500',
        text: <><b>{actor}</b> — {ev.event_type}</> };
  }
};

const TicketHistory = ({ ticketId, token, viewer, onOpenUser }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!ticketId || !token) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/tickets/${ticketId}/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Could not load history (${res.status})`);
        const data = await res.json();
        if (!cancelled) { setEvents(data); setError(''); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [ticketId, token]);

  // Newest first reads better on a long-running ticket -- the recent change is
  // usually why you opened the history.
  const ordered = [...events].reverse();
  const shown = expanded ? ordered : ordered.slice(0, 8);

  return (
    <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-n-600" />
        <h3 className="text-sm font-semibold text-n-900">History</h3>
        {!loading && events.length > 0 && (
          <span className="text-xs text-n-500">{events.length} entries</span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-n-500 py-4">Loading history...</div>
      ) : error ? (
        <div className="text-sm text-red-700 dark:text-red-400 py-4">{error}</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-n-500 py-4">
          Nothing recorded yet. History started when the audit trail was added, so
          older tickets show only changes made since then.
        </div>
      ) : (
        <>
          <ol className="relative">
            {shown.map((ev, i) => {
              const { icon: Icon, tone, text, body } = describe(ev, viewer, onOpenUser);
              const last = i === shown.length - 1;
              return (
                <li key={`${ev.event_type}-${ev.id}-${i}`} className="flex gap-3 pb-4 last:pb-0">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-7 h-7 rounded-full bg-n-100 flex items-center justify-center">
                      <Icon className={`w-3.5 h-3.5 ${tone}`} />
                    </div>
                    {!last && <div className="w-px flex-1 bg-n-200 mt-1" />}
                  </div>
                  <div className="min-w-0 flex-1 pt-1">
                    <p className="text-sm text-n-700">{text}</p>
                    {body && (
                      <p className="text-sm text-n-600 mt-1 p-2 bg-n-50 rounded border border-n-200 whitespace-pre-wrap break-words">
                        {body}
                      </p>
                    )}
                    <p className="text-xs text-n-500 mt-0.5">{when(ev.created_at)}</p>
                  </div>
                </li>
              );
            })}
          </ol>

          {ordered.length > 8 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs font-medium text-n-600 hover:text-n-900"
            >
              {expanded ? 'Show less' : `Show all ${ordered.length} entries`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default TicketHistory;
